<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import { useNotification } from "@kyvg/vue3-notification"
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import IconBase from '../../components/icon/IconBase.vue'
import IconCopy from '../../components/icon/IconCopy.vue'
import UserService from '@/services/UserService'
import FormButton from '../../components/Atoms/FormButton.vue'
import FormInput from '../../components/Atoms/FormInput.vue'
import IconChevronLeft from '../../components/icon/IconChevronLeft.vue'
import ModalBox from '../../components/modals/ModalBox.vue'
import WarningIconCustom from '../../components/icon/WarningIconCustom.vue'
import ButtonCopyToClipboard from '../../components/Moleculas/ButtonCopyToClipboard.vue'
import PasswordVisibilityInput from './PasswordVisibilityInput.vue'
import PasswordRequirementList from './PasswordRequirementList.vue'
import PasswordResetEmailNotice from './PasswordResetEmailNotice.vue'
import { track, EVENTS } from '@/lib/tracking'
import { getSigninUrl } from '@/Utils/authRedirect'

defineOptions({ name: 'ManageSecurity' })

// Props
const props = defineProps({
  showSetup: {
    type: Boolean,
    default: false
  },
  setupData: {
    type: Object,
    default: () => ({
      manualKey: '',
      backupCodes: [],
      qrCode: null
    })
  }
})

// Emit
const emit = defineEmits(['back', 'setup-start', 'setup-cancel'])

const { notify } = useNotification()
const store = useStore()

const isShowBackupCodes = ref(false)
const showDisableModal = ref(false)
const manualKey = ref('')
const backupCodes = ref([])
const digits = ref(['', '', '', '', '', ''])
const digitRefs = ref([])
const qrCode = ref(null)
const disableDigits = ref(['', '', '', '', '', ''])
const disableDigitRefs = ref([])
const isSettingUp = ref(false)
// Local gate so we don't flash the wrong Enable/Disable CTA, and so a stuck
 // global userLoaded=false (bootstrap still running / failed) can't spin forever.
const statusReady = ref(false)

const twoFactorEnabled = computed(() => store.getters['User/twoFactorEnabled'])
const passwordLoginEnabled = computed(() => store.getters['User/passwordLoginEnabled'])
const userLoaded = computed(() => store.getters['User/userLoaded'])

// Inside a team, only admins may change security settings. Checked against
// `admin` rather than `moderator` so older permission documents with an
// undefined role stay locked too.
const isTeamRestricted = computed(() =>
  Boolean(store.state.User.activatedTeam) && store.getters['User/userPermissions']?.role !== 'admin'
)

// ------------------------------------------------------ Password login state
const showPasswordEnableModal = ref(false)
const showPasswordChangeModal = ref(false)
const showPasswordDisableModal = ref(false)
const passwordProcessing = ref(false)
const passwordFormError = ref('')
const enableForm = ref({ code: '', password: '', passwordConfirm: '' })
const changeForm = ref({ currentPassword: '', newPassword: '', newPasswordConfirm: '' })
// Per-field show/hide toggles for the password inputs (ENG-4617 UX).
const visibleFields = ref({ enable: false, enableConfirm: false, current: false, next: false, confirm: false })

// Password policy checklist (ENG-4617 UX): min 8 chars, a number, a special.
function getPasswordChecks(value) {
  const password = value || ''
  return {
    minLength: password.length >= 8,
    hasNumber: /\d/.test(password),
    hasSpecial: /[^A-Za-z0-9]/.test(password)
  }
}
function allPasswordChecksMet(checks) {
  return checks.minLength && checks.hasNumber && checks.hasSpecial
}

const enableChecks = computed(() => getPasswordChecks(enableForm.value.password))
const changeChecks = computed(() => getPasswordChecks(changeForm.value.newPassword))
const enableMismatch = computed(() =>
  enableForm.value.passwordConfirm.length > 0 &&
  enableForm.value.passwordConfirm !== enableForm.value.password
)
const changeMismatch = computed(() =>
  changeForm.value.newPasswordConfirm.length > 0 &&
  changeForm.value.newPasswordConfirm !== changeForm.value.newPassword
)

const enableFormValid = computed(() =>
  /^\d{6}$/.test(enableForm.value.code.trim()) &&
  allPasswordChecksMet(enableChecks.value) &&
  enableForm.value.passwordConfirm === enableForm.value.password
)

// The emailed confirmation code is numeric: block non-digit keys as they're
// typed and sanitize whatever arrives via paste/autofill.
function blockNonDigitKey(evt) {
  if (
    evt.key &&
    evt.key.length === 1 &&
    !/\d/.test(evt.key) &&
    !evt.metaKey && !evt.ctrlKey && !evt.altKey
  ) {
    evt.preventDefault()
  }
}

function onEnableCodeInput(value) {
  const raw = String(value ?? '')
  const sanitized = raw.replace(/\D/g, '').slice(0, 6)
  if (sanitized === raw) {
    enableForm.value.code = sanitized
    return
  }
  // When sanitizing leaves the model unchanged (e.g. a letter pasted into an
  // empty field), Vue skips the re-render and the junk stays visible in the
  // DOM. Write the raw value first, then the clean one on the next tick, so
  // the input is always forced back to the sanitized state.
  enableForm.value.code = raw
  nextTick(() => {
    enableForm.value.code = sanitized
  })
}
const changeFormValid = computed(() =>
  changeForm.value.currentPassword.length > 0 &&
  allPasswordChecksMet(changeChecks.value) &&
  changeForm.value.newPasswordConfirm === changeForm.value.newPassword
)

// Password-mutating endpoints bump lastPasswordChanged, which invalidates the
// current JWT (lpc check) — they return a fresh session that must replace the
// stored one. UserService.setSession wipes the 2FA flags by design (new
// logins must re-verify), but here the same already-verified user keeps their
// session, so preserve the flags across the swap or the router guard would
// bounce a 2FA user back to the TOTP page.
function swapSession(user, authKey) {
  if (!user || !authKey) return
  const twoFactorRequired = localStorage.getItem('twoFactorRequired')
  const twoFactorValidated = localStorage.getItem('twoFactorValidated')
  UserService.setSession(user, authKey)
  if (twoFactorRequired !== null) localStorage.setItem('twoFactorRequired', twoFactorRequired)
  if (twoFactorValidated !== null) localStorage.setItem('twoFactorValidated', twoFactorValidated)
}

async function startPasswordEnable() {
  if (passwordProcessing.value) return
  passwordProcessing.value = true
  try {
    await UserService.requestPasswordLoginCode()
    enableForm.value = { code: '', password: '', passwordConfirm: '' }
    passwordFormError.value = ''
    visibleFields.value = { enable: false, enableConfirm: false, current: false, next: false, confirm: false }
    showPasswordEnableModal.value = true
    notify({ group: 'success', text: 'We emailed you a 6-digit confirmation code.' })
  } catch (e) {
    notify({ group: 'error', text: e?.message || 'Failed to send the confirmation code' })
  } finally {
    passwordProcessing.value = false
  }
}

async function confirmPasswordEnable() {
  if (passwordProcessing.value || !enableFormValid.value) return
  passwordFormError.value = ''
  if (enableForm.value.password !== enableForm.value.passwordConfirm) {
    passwordFormError.value = 'Passwords do not match.'
    return
  }
  passwordProcessing.value = true
  try {
    const response = await UserService.enablePasswordLogin({
      code: enableForm.value.code.trim(),
      password: enableForm.value.password,
      passwordConfirm: enableForm.value.passwordConfirm
    })
    swapSession(response.user, response.authKey)
    store.dispatch('User/setPasswordLoginEnabled', true)
    // Drop cached /users/me so a later loadUser can't restore pre-enable state.
    localStorage.removeItem('castr-cache/users/me')
    track(EVENTS.PASSWORD_LOGIN_ENABLED, {})
    notify({ group: 'success', text: 'Password login enabled.' })
    showPasswordEnableModal.value = false
    enableForm.value = { code: '', password: '', passwordConfirm: '' }
  } catch (e) {
    passwordFormError.value = e?.message || 'Failed to enable password login'
  } finally {
    passwordProcessing.value = false
  }
}

async function confirmPasswordChange() {
  if (passwordProcessing.value || !changeFormValid.value) return
  passwordFormError.value = ''
  if (changeForm.value.newPassword !== changeForm.value.newPasswordConfirm) {
    passwordFormError.value = 'New passwords do not match.'
    return
  }
  passwordProcessing.value = true
  try {
    const response = await UserService.changePassword({
      currentPassword: changeForm.value.currentPassword,
      newPassword: changeForm.value.newPassword,
      newPasswordConfirm: changeForm.value.newPasswordConfirm
    })
    // Older backends returned only { message } — swapSession no-ops then, and
    // the user will simply be asked to log in again.
    swapSession(response?.user, response?.authKey)
    localStorage.removeItem('castr-cache/users/me')
    track(EVENTS.ACCOUNT_PASSWORD_CHANGED, {})
    notify({ group: 'success', text: 'Password changed.' })
    showPasswordChangeModal.value = false
    changeForm.value = { currentPassword: '', newPassword: '', newPasswordConfirm: '' }
  } catch (e) {
    passwordFormError.value = e?.message || 'Failed to change password'
  } finally {
    passwordProcessing.value = false
  }
}

async function confirmPasswordDisable() {
  if (passwordProcessing.value) return
  passwordProcessing.value = true
  try {
    const response = await UserService.disablePasswordLogin()
    swapSession(response.user, response.authKey)
    store.dispatch('User/setPasswordLoginEnabled', false)
    localStorage.removeItem('castr-cache/users/me')
    track(EVENTS.PASSWORD_LOGIN_DISABLED, {})
    notify({ group: 'success', text: 'Password login disabled.' })
    showPasswordDisableModal.value = false
  } catch (e) {
    notify({ group: 'error', text: e?.message || 'Failed to disable password login' })
  } finally {
    passwordProcessing.value = false
  }
}

function openPasswordChange() {
  changeForm.value = { currentPassword: '', newPassword: '', newPasswordConfirm: '' }
  passwordFormError.value = ''
  visibleFields.value = { enable: false, enableConfirm: false, current: false, next: false, confirm: false }
  showPasswordChangeModal.value = true
}

function cancelPasswordModals() {
  showPasswordEnableModal.value = false
  showPasswordChangeModal.value = false
  showPasswordDisableModal.value = false
  passwordFormError.value = ''
  enableForm.value = { code: '', password: '', passwordConfirm: '' }
  changeForm.value = { currentPassword: '', newPassword: '', newPasswordConfirm: '' }
  visibleFields.value = { enable: false, enableConfirm: false, current: false, next: false, confirm: false }
}

// ---------------------------------------------- Forgot password (ENG-4617 UX)
// Escape hatch for users who opened "Change password" but can't remember the
// current one: email them a reset link, then sign this session out.
const resetProcessing = ref(false)
const showResetNotice = ref(false)
const signingOut = ref(false)

const userEmail = computed(() =>
  UserService.getUserEmail() || store.getters['User/userEmail'] || ''
)

async function forgotPassword() {
  if (resetProcessing.value) return
  passwordFormError.value = ''
  const email = (userEmail.value || '').trim().toLowerCase()
  if (!email) {
    notify({ group: 'error', text: 'Could not find your account email.' })
    return
  }
  resetProcessing.value = true
  try {
    await UserService.requestPasswordReset(email)
    track(EVENTS.PASSWORD_RESET_REQUESTED, { source: 'security_settings' })
    showPasswordChangeModal.value = false
    showResetNotice.value = true
  } catch (e) {
    const message = e?.message || 'Failed to send the password reset email.'
    passwordFormError.value = message
    notify({ group: 'error', text: message })
  } finally {
    resetProcessing.value = false
  }
}

async function backToLogin() {
  if (signingOut.value) return
  signingOut.value = true
  try {
    track(EVENTS.LOGGED_OUT, { source: 'password_reset_notice' })
    await store.dispatch('Auth/logout')
  } catch (e) {
    console.error('Failed to clear the session before redirecting to login', e)
  }
  // Full reload so no cached in-memory user state survives the sign-out.
  window.location.href = getSigninUrl()
}

// -------------------------------------- Force logout all devices (ENG-4617)
// Bumps tokenVersion server-side: every JWT (including this one) dies and live
// sockets are evicted, so we must send the user back to sign-in afterwards.
const showRevokeModal = ref(false)
const revokeProcessing = ref(false)

async function confirmRevokeSessions() {
  if (revokeProcessing.value) return
  revokeProcessing.value = true
  try {
    await UserService.revokeAllSessions()
    track(EVENTS.ALL_SESSIONS_REVOKED, {})
    notify({ group: 'success', text: 'All devices have been signed out.' })
    await backToLogin()
  } catch (e) {
    notify({ group: 'error', text: e?.message || 'Failed to sign out other devices' })
    revokeProcessing.value = false
    showRevokeModal.value = false
  }
}
const isCodeComplete = computed(() => digits.value.every(v => v && v.length === 1))
const code = computed(() => digits.value.join(''))
const isDisableCodeComplete = computed(() => disableDigits.value.every(v => v && v.length === 1))
const disableCode = computed(() => disableDigits.value.join(''))

async function ensureTwoFactorStatus() {
  if (userLoaded.value) {
    statusReady.value = true
    return
  }
  try {
    await store.dispatch('User/loadUser')
  } catch (e) {
    console.error('Failed to load user for 2FA status', e)
  } finally {
    statusReady.value = true
  }
}

onMounted(ensureTwoFactorStatus)
watch(userLoaded, (loaded) => {
  if (loaded) statusReady.value = true
})

const twoFactorSetup = async () => {
  try {
    isSettingUp.value = true
    const response = await UserService.twoFactorSetup()
    // Do NOT flip store twoFactorEnabled here — setup UI is driven by showSetup.
    // Marking enabled before confirm made cancel/back wipe a real enabled state.

    const setupData = {
      manualKey: response.secret,
      backupCodes: response.backupCodes,
      qrCode: response.qrCode
    }

    // Emit event to parent to show setup view with data
    emit('setup-start', setupData)
  } catch (e) {
    console.error('2FA Setup Error:', e) // Debug log
    notify({ group: 'error', text: 'Failed to enable 2FA' })
  } finally {
    isSettingUp.value = false
  }
}

const cancelSetup = () => {
  digits.value = ['', '', '', '', '', '']
  backupCodes.value = []
  
  // Emit event to parent to hide setup view. Leave store twoFactorEnabled alone —
  // cancel must not clear an already-enabled account's flag.
  emit('setup-cancel')
}

const confirmSetup = async () => {
  try {
    await UserService.twoFactorEnable(code.value)
    track(EVENTS.TWO_FACTOR_ENABLED, {})
    notify({ group: 'success', text: '2FA code confirmed.' })
    store.dispatch('User/setTwoFactorEnabled', true)
    // Drop cached /users/me so a later loadUser can't restore pre-enable state.
    localStorage.removeItem('castr-cache/users/me')
    digits.value = ['', '', '', '', '', '']
    backupCodes.value = []
    // Emit event to parent to hide setup view
    emit('setup-cancel')
  } catch (error) {
    notify({ group: 'error', text: 'Failed to enable 2FA' })
  }
}

const twoFactorDisable = () => {
  showDisableModal.value = true
}

const confirmDisable = async () => {
  try {
    await UserService.twoFactorDisable(disableCode.value)
    track(EVENTS.TWO_FACTOR_DISABLED, {})
    notify({ group: 'success', text: '2FA disabled.' })
    store.dispatch('User/setTwoFactorEnabled', false)
    localStorage.removeItem('castr-cache/users/me')
    cancelDisable()
  } catch (error) {
    notify({ group: 'error', text: 'Failed to disable 2FA' })
    disableDigits.value = ['', '', '', '', '', '']
  }
}

const cancelDisable = () => {
  showDisableModal.value = false
  disableDigits.value = ['', '', '', '', '', '']
}

const handleBack = () => {
  emit('back')
}
const copy = async (text) => {
  try {
    await navigator.clipboard.writeText(text)
    notify({ group: 'info', text: 'Copied' })
  } catch (e) { }
}
const showBackupCodes = () => {
  isShowBackupCodes.value = !isShowBackupCodes.value
}
const onDigitInput = (index, evt) => {
  const value = (evt.target.value || '').replace(/\D/g, '').slice(0, 1)
  digits.value[index] = value
  if (value && index < digits.value.length - 1 && digitRefs.value[index + 1]) {
    digitRefs.value[index + 1].focus()
  }
}
const onBackspace = (index) => {
  if (!digits.value[index] && index > 0 && digitRefs.value[index - 1]) {
    digitRefs.value[index - 1].focus()
    digits.value[index - 1] = ''
  } else {
    digits.value[index] = ''
  }
}
const onPaste = (index, evt) => {
  evt.preventDefault()
  const paste = (evt.clipboardData || window.clipboardData).getData('text') || ''
  const chars = paste.replace(/\D/g, '').slice(0, digits.value.length - index).split('')
  if (!chars.length) return
  for (let i = 0; i < chars.length; i++) {
    digits.value[index + i] = chars[i]
  }
  const nextIndex = Math.min(index + chars.length, digits.value.length - 1)
  if (digitRefs.value[nextIndex]) digitRefs.value[nextIndex].focus()
}

// Disable modal input handlers
const onDisableDigitInput = (index, evt) => {
  const value = (evt.target.value || '').replace(/\D/g, '').slice(0, 1)
  disableDigits.value[index] = value
  if (value && index < disableDigits.value.length - 1 && disableDigitRefs.value[index + 1]) {
    disableDigitRefs.value[index + 1].focus()
  }
}

const onDisableBackspace = (index) => {
  if (!disableDigits.value[index] && index > 0 && disableDigitRefs.value[index - 1]) {
    disableDigitRefs.value[index - 1].focus()
    disableDigits.value[index - 1] = ''
  } else {
    disableDigits.value[index] = ''
  }
}

const onDisablePaste = (index, evt) => {
  evt.preventDefault()
  const paste = (evt.clipboardData || window.clipboardData).getData('text') || ''
  const chars = paste.replace(/\D/g, '').slice(0, disableDigits.value.length - index).split('')
  if (!chars.length) return
  for (let i = 0; i < chars.length; i++) {
    disableDigits.value[index + i] = chars[i]
  }
  const nextIndex = Math.min(index + chars.length, disableDigits.value.length - 1)
  if (disableDigitRefs.value[nextIndex]) disableDigitRefs.value[nextIndex].focus()
}
</script>

<template>
  <!-- No min-height here: the settings list should hug its content. The 2FA
       setup view below carries its own mh-60 so its vertical centering works. -->
  <div class="flex flex-col w-full">
    <!-- Back Button -->
    <div v-if="props.showSetup" class="back-button-container my-6">
      <FormButton custom-classes="bg-transparent flex text-sm items-center gap-2" @click="handleBack">
        <IconBase class-icon="bg-surface-2 p-1 rounded-full size-6">
          <IconChevronLeft />
        </IconBase>
        <span>Two-Factor Authentication</span>
      </FormButton>
    </div>

    <!-- Initial Security View -->
    <p v-if="!props.showSetup && isTeamRestricted" class="text-surface-7 text-sm mb-4">
      Security settings can't be changed while you're working in a team.
    </p>
    <div class="w-full divide-y divide-surface-3" v-if="!props.showSetup">
      <div class="flex flex-col gap-3 pb-5 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
        <div class="min-w-0">
          <h3 class="text-base font-medium mb-1">Password login</h3>
          <p class="text-surface-7 text-sm mb-0">
            Log in with your email and a password instead of an emailed code
          </p>
        </div>
        <div class="flex flex-wrap items-center gap-2 sm:shrink-0 sm:justify-end">
          <FormButton v-if="!statusReady" type="secondary" disabled :isLoading="true" />
          <FormButton v-else-if="passwordLoginEnabled !== true" @click="startPasswordEnable"
            :disabled="passwordProcessing || isTeamRestricted">
            {{ passwordProcessing ? 'Sending code...' : 'Enable password login' }}
          </FormButton>
          <template v-else>
            <FormButton type="secondary" :disabled="isTeamRestricted" @click="openPasswordChange">Change password</FormButton>
            <FormButton type="secondary" :disabled="isTeamRestricted" @click="showPasswordDisableModal = true">Disable</FormButton>
          </template>
        </div>
      </div>

      <div class="flex flex-col gap-3 py-5 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
        <div class="min-w-0">
          <h3 class="text-base font-medium mb-1">Two-Factor Authentication</h3>
          <p class="text-surface-7 text-sm mb-0">Add an extra layer of security to your account with 2FA</p>
        </div>
        <div class="flex flex-wrap items-center gap-2 sm:shrink-0 sm:justify-end">
          <FormButton v-if="!statusReady" type="secondary" disabled :isLoading="true" />
          <FormButton v-else-if="twoFactorEnabled !== true" @click="twoFactorSetup" :disabled="isSettingUp || isTeamRestricted">
            {{ isSettingUp ? 'Setting up...' : 'Enable 2FA' }}
          </FormButton>
          <FormButton type="secondary" v-else :disabled="isTeamRestricted" @click="twoFactorDisable">Disable 2FA</FormButton>
        </div>
      </div>

      <div class="flex flex-col gap-3 pt-5 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
        <div class="min-w-0">
          <h3 class="text-base font-medium mb-1">Active sessions</h3>
          <p class="text-surface-7 text-sm mb-0">
            Sign out of Castr on every device, including this one
          </p>
        </div>
        <div class="flex flex-wrap items-center gap-2 sm:shrink-0 sm:justify-end">
          <FormButton type="secondary" :disabled="revokeProcessing || isTeamRestricted" @click="showRevokeModal = true">
            Log out all devices
          </FormButton>
        </div>
      </div>
    </div>

    <!-- Setup 2FA View -->
    <div class="w-full grow-1 mh-60 flex flex-col justify-center" v-else>
      <h3 class="text-center text-xl font-medium mb-3">Scan QR & Save Backup Codes</h3>
      <p class="text-center text-surface-7 text-sm mb-4">Scan the QR code with your authenticator app and save your
        backup
        codes</p>

      <div class="flex flex-col items-center mb-4">
        <div class="mb-4">
          <div v-if="props.setupData.qrCode" class="size-[240px] !rounded-lg overflow-hidden"
            v-html="props.setupData.qrCode"></div>
        </div>

        <div class="bg-surface-2 mb-3 p-3 rounded-lg w-full lg:w-[400px]">
          <h5 class="text-sm !mb-2">Manual Entry Key</h5>
          <div class="flex items-center relative">
            <FormInput customClasses="grow-1 !pr-8" readonly :modelValue="props.setupData.manualKey" />
            <ButtonCopyToClipboard class="absolute right-0 top-0 z-10" :text="props.setupData.manualKey" />
          </div>
        </div>

        <div class="bg-surface-2 mb-3 p-3 rounded-lg w-full lg:w-[400px]">
          <div class="flex items-center justify-between">
            <h5 class="text-sm !mb-2">Backup codes</h5>
            <FormButton custom-classes="text-main text-sm" @click="showBackupCodes">{{ isShowBackupCodes ? 'Hide' :
              'Show'
              }}</FormButton>
          </div>
          <ul v-if="isShowBackupCodes" class="mt-3 grid grid-cols-2 md:grid-cols-3 text-sm gap-3">
            <li class="flex items-center justify-center relative"
              v-for="(code, idx) in props.setupData.backupCodes" :key="idx">
              <FormInput customClasses="grow-1 !pr-6" readonly :modelValue="code" />
              <ButtonCopyToClipboard size="md" class="absolute right-0 top-0 z-10 shrink-0" :text="code" />
            </li>
          </ul>
        </div>

        <div class="bg-surface-2 mb-3 p-3 rounded-lg w-full lg:w-[400px]">
          <h5 class="text-sm !mb-2">Enter the 6-digit code from your authenticator app</h5>
          <div class="flex items-center gap-3 justify-center min-w-0 max-w-full">
            <input v-for="(d, i) in digits" :key="i" :ref="el => (digitRefs[i] = el)"
              class="shrink grow-0 min-w-0 bg-surface-3 h-14 rounded-lg text-center" type="text" maxlength="1"
              v-model="digits[i]" @input="onDigitInput(i, $event)" @keydown.backspace.prevent="onBackspace(i, $event)"
              @paste="onPaste(i, $event)" :autofocus="i === 0">
          </div>

          <div class="flex items-center gap-3 mt-3">
            <FormButton size="lg" type="secondary" class="grow-1 text-center justify-center basis-1/2" @click="cancelSetup">Cancel</FormButton>
            <FormButton size="lg" class="grow-1 text-center justify-center basis-1/2" :disabled="!isCodeComplete" @click="confirmSetup">Confirm</FormButton>
          </div>
        </div>


      </div>
    </div>

    <!-- Enable password login: emailed code + choose a password -->
    <ModalBox v-model="showPasswordEnableModal" size="sm" hide-close>
      <section class="!px-5">
        <h2 class="text-center text-base font-medium">Enable password login</h2>
        <p class="text-center text-surface-8 text-sm mb-6">
          Enter the 6-digit code we emailed you, then choose a password.
        </p>

        <div class="flex flex-col gap-3">
          <FormInput label="Confirmation code" placeholder="6-digit code" type="tel" :maxlength="6"
            :model-value="enableForm.code" @update:model-value="onEnableCodeInput"
            @keydown="blockNonDigitKey" />
          <PasswordVisibilityInput id="enable-login-password" label="New password"
            autocomplete="new-password" v-model="enableForm.password"
            :visible="visibleFields.enable" :is-error="!!passwordFormError"
            @toggle-visible="visibleFields.enable = !visibleFields.enable" />
          <PasswordVisibilityInput id="enable-login-password-confirm" label="Confirm new password"
            autocomplete="new-password" v-model="enableForm.passwordConfirm"
            :visible="visibleFields.enableConfirm" :is-error="enableMismatch"
            :helper="enableMismatch ? 'Passwords do not match.' : ''"
            @toggle-visible="visibleFields.enableConfirm = !visibleFields.enableConfirm" />
          <PasswordRequirementList :checks="enableChecks" />
          <p v-if="passwordFormError" class="text-error text-sm mb-0">{{ passwordFormError }}</p>
        </div>
      </section>

      <template v-slot:footer>
        <div class="flex gap-3 w-full !px-5 !pb-5 !pt-2 mt-6">
          <FormButton class="text-center justify-center grow basis-1/2" size="lg" type="secondary"
            @click="cancelPasswordModals">
            Cancel
          </FormButton>
          <FormButton class="text-center justify-center grow basis-1/2" size="lg"
            :disabled="!enableFormValid || passwordProcessing" @click="confirmPasswordEnable">
            {{ passwordProcessing ? 'Enabling...' : 'Enable' }}
          </FormButton>
        </div>
      </template>
    </ModalBox>

    <!-- Change password (current + new twice) -->
    <ModalBox v-model="showPasswordChangeModal" size="sm" hide-close>
      <section class="!px-5">
        <h2 class="text-center text-base font-medium">Change password</h2>
        <p class="text-center text-surface-8 text-sm mb-6">
          Other devices will be signed out after the change.
        </p>

        <div class="flex flex-col gap-3">
          <PasswordVisibilityInput id="change-current-password" label="Current password"
            autocomplete="current-password" v-model="changeForm.currentPassword"
            :visible="visibleFields.current"
            @toggle-visible="visibleFields.current = !visibleFields.current" />
          <PasswordVisibilityInput id="change-new-password" label="New password"
            autocomplete="new-password" v-model="changeForm.newPassword"
            :visible="visibleFields.next" :is-error="!!passwordFormError"
            @toggle-visible="visibleFields.next = !visibleFields.next" />
          <PasswordVisibilityInput id="change-new-password-confirm" label="Confirm new password"
            autocomplete="new-password" v-model="changeForm.newPasswordConfirm"
            :visible="visibleFields.confirm" :is-error="changeMismatch"
            :helper="changeMismatch ? 'Passwords do not match.' : ''"
            @toggle-visible="visibleFields.confirm = !visibleFields.confirm" />
          <PasswordRequirementList :checks="changeChecks" />
          <p v-if="passwordFormError" class="text-error text-sm mb-0">{{ passwordFormError }}</p>
          <FormButton type="link" custom-classes="text-main text-sm p-0 self-start"
            :disabled="resetProcessing" @click="forgotPassword">
            {{ resetProcessing ? 'Sending reset email...' : 'Forgot your password?' }}
          </FormButton>
        </div>
      </section>

      <template v-slot:footer>
        <div class="flex gap-3 w-full !px-5 !pb-5 !pt-2 mt-6">
          <FormButton class="text-center justify-center grow basis-1/2" size="lg" type="secondary"
            @click="cancelPasswordModals">
            Cancel
          </FormButton>
          <FormButton class="text-center justify-center grow basis-1/2" size="lg"
            :disabled="!changeFormValid || passwordProcessing" @click="confirmPasswordChange">
            {{ passwordProcessing ? 'Saving...' : 'Save' }}
          </FormButton>
        </div>
      </template>
    </ModalBox>

    <!-- Disable password login confirmation -->
    <ModalBox v-model="showPasswordDisableModal" size="sm" hide-close>
      <section class="!px-5">
        <WarningIconCustom class="mx-auto mb-6" />
        <h2 class="text-center text-base font-medium">Disable password login?</h2>
        <p class="text-center text-surface-8 text-sm mb-6">
          We'll remove your saved password and switch you back to signing in with a
          one-time code sent to your email. You can turn password login back on anytime.
        </p>
      </section>

      <template v-slot:footer>
        <div class="flex gap-3 w-full !px-5 !pb-5 !pt-2 mt-6">
          <FormButton class="text-center justify-center grow basis-1/2" size="lg" type="secondary"
            @click="cancelPasswordModals">
            Cancel
          </FormButton>
          <FormButton type="red" class="text-center justify-center grow basis-1/2" size="lg"
            :disabled="passwordProcessing" @click="confirmPasswordDisable">
            {{ passwordProcessing ? 'Disabling...' : 'Disable' }}
          </FormButton>
        </div>
      </template>
    </ModalBox>

    <ModalBox v-model="showDisableModal" size="sm" @close="closeModal" hide-close>
      <section class="!px-5">
        <WarningIconCustom class="mx-auto mb-6" />
        <h2 class="text-center text-base font-medium">Disable Two-Factor Authentication?</h2>
        <p class="text-center text-surface-8 text-sm mb-6">Enter your authenticator code to confirm disabling 2FA.</p>

        <div class="flex items-center gap-3 justify-center min-w-0 max-w-full mx-8">
          <input class="shrink grow-0 min-w-0 bg-surface-3 h-14 rounded-lg text-center" v-for="(d, i) in disableDigits" :key="i" :ref="el => (disableDigitRefs[i] = el)"
            type="text" maxlength="1" v-model="disableDigits[i]" @input="onDisableDigitInput(i, $event)"
            @keydown.backspace.prevent="onDisableBackspace(i, $event)" @paste="onDisablePaste(i, $event)">
        </div>
      </section>

      <template v-slot:footer>
        <div class="flex gap-3 w-full !px-5 !pb-5 !pt-2 mt-6">
          <FormButton class="text-center justify-center grow basis-1/2" size="lg" @click="cancelDisable" type="secondary">
            Cancel
          </FormButton>
          <FormButton type="red" class="text-center justify-center grow basis-1/2" size="lg" :disabled="!isDisableCodeComplete" @click="confirmDisable">
            Disable
          </FormButton>
        </div>
      </template>
    </ModalBox>
    <!-- Disable 2FA Modal -->

    <!-- Force logout all devices confirmation -->
    <ModalBox v-model="showRevokeModal" size="sm" hide-close>
      <section class="!px-5">
        <WarningIconCustom class="mx-auto mb-6" />
        <h2 class="text-center text-base font-medium">Log out all devices?</h2>
        <p class="text-center text-surface-8 text-sm mb-6">
          Every session will be signed out immediately, including this one.
          You'll need to log in again.
        </p>
      </section>

      <template v-slot:footer>
        <div class="flex gap-3 w-full !px-5 !pb-5 !pt-2 mt-6">
          <FormButton class="text-center justify-center grow basis-1/2" size="lg" type="secondary"
            @click="showRevokeModal = false">
            Cancel
          </FormButton>
          <FormButton type="red" class="text-center justify-center grow basis-1/2" size="lg"
            :disabled="revokeProcessing" @click="confirmRevokeSessions">
            {{ revokeProcessing ? 'Signing out...' : 'Log out all' }}
          </FormButton>
        </div>
      </template>
    </ModalBox>

    <!-- Full-page confirmation after the reset email goes out -->
    <PasswordResetEmailNotice v-model="showResetNotice" :email="userEmail"
      :processing="signingOut" @back-to-login="backToLogin" />
  </div>

</template>

<style scoped>
/* keep consistent styling with other manage pages */
</style>
